import { useCallback, useLayoutEffect, useRef } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

interface UseAnnotationsPanelResult {
  isOpen: boolean;
  toggle: () => void;
  setOpen: (open: boolean) => void;
}

/**
 * Returns the effective open state of the docked annotations panel along with
 * helpers to toggle it.
 *
 * The persisted preference (in `useBuildPreferencesStore`) drives the open
 * state, except in two cases where the panel auto-opens via a session-scoped
 * override:
 *
 * 1. On initial render of a build that has annotations.
 * 2. When new build-scope annotations subsequently arrive over Action Cable
 *    (handled in `BuildContext` by setting the override directly on the
 *    build store).
 *
 * The user can always close the panel after either auto-open; closing also
 * persists the preference. If new annotations arrive again afterwards the
 * panel re-opens.
 *
 * The override is reset whenever the active build changes so that auto-open
 * behaviour is re-evaluated per build (e.g. when navigating between builds).
 */
export function useAnnotationsPanel(): UseAnnotationsPanelResult {
  const { build } = useBuild();
  const persistedOpen = useBuildPreferencesStore((state) => state.annotationsPanelOpen);
  const setPersistedOpen = useBuildPreferencesStore((state) => state.setAnnotationsPanelOpen);
  const override = useBuildStore((state) => state.annotationsPanelOverride);
  const setOverride = useBuildStore((state) => state.setAnnotationsPanelOverride);

  // On first render of each build, auto-open the panel if annotations exist.
  // useLayoutEffect runs synchronously after render but before paint, which
  // avoids a flicker between the persisted preference and the auto-open state.
  const seenBuildIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (seenBuildIdRef.current === build.id) {
      return;
    }
    seenBuildIdRef.current = build.id;

    if (getAnnotationCounts(build).totalCount > 0) {
      setOverride(true);
    } else {
      setOverride(null);
    }
  }, [build, setOverride]);

  const isOpen = override !== null ? override : persistedOpen;

  const setOpen = useCallback(
    (open: boolean) => {
      setPersistedOpen(open);
      setOverride(open);
    },
    [setPersistedOpen, setOverride],
  );

  const toggle = useCallback(() => setOpen(!isOpen), [isOpen, setOpen]);

  return { isOpen, toggle, setOpen };
}
